import { useCallback, useState, type FormEvent, type ReactElement } from 'react';
import { Link, useParams } from 'react-router-dom';
import type {
  AdminClientDetail,
  Device,
  EnrollmentTokenResponse,
  SessionSummary,
} from '@opsrev/shared';
import { getClient, rotateToken, updateClient } from '../api';
import { useAsync } from '../useAsync';
import { absoluteTime, formatDuration, relativeTime } from '../format';
import { DeviceStatusBadge, EmptyState, ErrorState, SessionStatusChip, Spinner } from '../components/Status';
import { Modal, CopyField } from '../components/Modal';
import { EnrollmentInstructions } from '../components/EnrollmentInstructions';

function OpsRevOrgMapping({
  clientId,
  initialValue,
  onSaved,
}: {
  clientId: string;
  initialValue: string | null;
  onSaved: () => void;
}): ReactElement {
  const [value, setValue] = useState(initialValue ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  async function submit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      await updateClient(clientId, { opsrevOrgId: value.trim() || null });
      setSaved(true);
      onSaved();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Unable to save organization mapping.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <form className="card" onSubmit={submit}>
      <p className="muted" style={{ marginTop: 0 }}>
        Map this Capture client to the exact tenant identifier issued by OpsRev Auth. Until this is
        set, the control plane cannot read its sessions.
      </p>
      <label className="field">
        <span>OpsRev organization ID</span>
        <input
          value={value}
          placeholder="org_…"
          autoComplete="off"
          onChange={(event) => {
            setValue(event.target.value);
            setSaved(false);
          }}
        />
      </label>
      {error ? <p className="error-text">{error}</p> : null}
      {saved ? <p>Organization mapping saved.</p> : null}
      <div className="actions-row">
        <button className="btn primary" type="submit" disabled={saving}>
          {saving ? 'Saving…' : 'Save mapping'}
        </button>
      </div>
    </form>
  );
}

function DevicesTable({ devices }: { devices: Device[] }): ReactElement {
  if (devices.length === 0) {
    return <EmptyState title="No devices" hint="No installs have registered yet." />;
  }
  return (
    <div className="table-card">
      <table className="data-table">
        <thead>
          <tr>
            <th>Device</th>
            <th>Status</th>
            <th>Last seen</th>
            <th>Platform</th>
            <th>App version</th>
            <th>Hostname</th>
          </tr>
        </thead>
        <tbody>
          {devices.map((d) => (
            <tr key={d.id}>
              <td className="strong">{d.label ?? d.deviceUid}</td>
              <td>
                <DeviceStatusBadge status={d.status} />
              </td>
              <td title={absoluteTime(d.lastSeenAt)}>{relativeTime(d.lastSeenAt)}</td>
              <td>{d.platform ?? '—'}</td>
              <td>{d.appVersion ?? '—'}</td>
              <td className="muted">{d.hostname ?? '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SessionsTable({ sessions }: { sessions: SessionSummary[] }): ReactElement {
  if (sessions.length === 0) {
    return <EmptyState title="No sessions" hint="Recorded sessions will appear here." />;
  }
  return (
    <div className="table-card">
      <table className="data-table">
        <thead>
          <tr>
            <th>Task</th>
            <th>Role</th>
            <th>Status</th>
            <th>Duration</th>
            <th>Recorded</th>
          </tr>
        </thead>
        <tbody>
          {sessions.map((s) => (
            <tr key={s.id}>
              <td>
                <Link to={`/sessions/${s.id}`} className="row-link">
                  {s.taskTitle || '(untitled)'}
                </Link>
              </td>
              <td>{s.userRole || '—'}</td>
              <td>
                <SessionStatusChip status={s.status} />
              </td>
              <td>{formatDuration(s.durationMs)}</td>
              <td title={absoluteTime(s.createdAt)}>{relativeTime(s.createdAt)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RotateTokenAction({ clientId }: { clientId: string }): ReactElement {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<EnrollmentTokenResponse | null>(null);

  const reset = () => {
    setOpen(false);
    setBusy(false);
    setError(null);
    setResult(null);
  };

  const rotate = async () => {
    setBusy(true);
    setError(null);
    try {
      const resp = await rotateToken(clientId);
      setResult(resp);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to rotate token.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button className="btn" onClick={() => setOpen(true)}>
        Rotate token
      </button>
      {open ? (
        <Modal title="Rotate enrollment token" onClose={reset} width={result ? 620 : 460}>
          {result ? (
            <>
              <EnrollmentInstructions enrollmentToken={result.enrollmentToken} rotated />
              <div className="modal-actions">
                <button className="btn primary" onClick={reset}>
                  Done
                </button>
              </div>
            </>
          ) : (
            <>
              <p>
                Rotating issues a <strong>new</strong> enrollment token and
                immediately invalidates the old one. Existing installs using the
                old token will stop authenticating until re-enrolled.
              </p>
              {error ? <p className="error-text">{error}</p> : null}
              <div className="modal-actions">
                <button className="btn" onClick={reset} disabled={busy}>
                  Cancel
                </button>
                <button className="btn primary" onClick={rotate} disabled={busy}>
                  {busy ? 'Rotating…' : 'Rotate token'}
                </button>
              </div>
            </>
          )}
        </Modal>
      ) : null}
    </>
  );
}

export function ClientDetail(): ReactElement {
  const { id = '' } = useParams();
  const loader = useCallback(() => getClient(id), [id]);
  const { data, loading, error, reload } = useAsync<AdminClientDetail>(loader, [id]);

  if (loading && !data) return <Spinner label="Loading client…" />;
  if (error) return <ErrorState message={error} onRetry={reload} />;
  if (!data) return <EmptyState title="Client not found" />;

  const { client, devices, sessions } = data;

  return (
    <div className="page">
      <div className="breadcrumb">
        <Link to="/">Clients</Link>
        <span>/</span>
        <span>{client.name}</span>
      </div>

      <div className="page-head">
        <div>
          <h1>{client.name}</h1>
          <p className="muted">
            <span className={`badge client-${client.status}`}>
              {client.status === 'active' ? 'Active' : 'Disabled'}
            </span>{' '}
            Created {absoluteTime(client.createdAt)}
            {client.createdBy ? ` by ${client.createdBy}` : ''}
          </p>
        </div>
        <div className="actions-row">
          <RotateTokenAction clientId={client.id} />
        </div>
      </div>

      <section className="section">
        <h2>OpsRev tenant</h2>
        <OpsRevOrgMapping
          key={client.opsrevOrgId ?? 'unmapped'}
          clientId={client.id}
          initialValue={client.opsrevOrgId}
          onSaved={reload}
        />
      </section>

      <section className="section">
        <h2>Enrollment</h2>
        {client.enrollmentToken ? (
          <div className="card">
            <p className="muted" style={{ marginTop: 0 }}>
              Give this token to whoever installs the OpsRev Capture app — it
              connects their install to this client. The same token works for
              every device, so reuse it for new installs instead of rotating.
            </p>
            <CopyField label="Enrollment token" value={client.enrollmentToken} />
          </div>
        ) : (
          <EmptyState
            title="No viewable token yet"
            hint="This client was created before tokens were viewable. Use “Rotate token” above once to issue a token you can view and reuse here — note that rotating invalidates any token already in use."
          />
        )}
      </section>

      <section className="section">
        <h2>Sessions ({sessions.length})</h2>
        <SessionsTable sessions={sessions} />
      </section>

      <section className="section">
        <h2>Devices ({devices.length})</h2>
        <DevicesTable devices={devices} />
      </section>
    </div>
  );
}
